import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import NavBar from './components/NavBar';
import Home from './pages/Home';
import WinRate from './pages/WinRate';
import TierList from './pages/TierList';
import { 
  Box, 
  Container, 
  Typography, 
  Button,
  Grid, 
  Paper, 
  Tabs, 
  Tab, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  CircularProgress 
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ClearIcon from '@mui/icons-material/Clear';
import { fetchHeroes, fetchMaps, fetchRanks } from './services/dataService';
import { findBestCounters } from './utils/counterLogic';
import HeroSelection from './components/HeroSelection';
import BannedHeroes from './components/BannedHeroes';
import CounterDisplay from './components/CounterDisplay';
import MapRankSelection from './components/MapRankSelection';

// Renamed from AppContent to MainContent to avoid confusion
function MainContent() {
  const { t } = useLanguage();
  const [heroes, setHeroes] = useState([]);
  const [maps, setMaps] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [selectedEnemies, setSelectedEnemies] = useState([]);
  const [selectedAllies, setSelectedAllies] = useState([]);
  const [bannedHeroes, setBannedHeroes] = useState([]);
  const [selectedMap, setSelectedMap] = useState('');
  const [selectedRank, setSelectedRank] = useState('');
  const [counterPicks, setCounterPicks] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [disclaimerOpen, setDisclaimerOpen] = useState(true);
  const counterSectionRef = React.useRef(null);

  const getRoleColor = (role) => {
    switch(role) {
      case 'VANGUARD': return '#3498db';
      case 'DUELIST': return '#e74c3c';
      case 'STRATEGIST': return '#2ecc71';
      default: return '#fbdb2b';
    }
  };

  useEffect(() => {
    if (selectedEnemies.length === 6 && counterSectionRef.current) {
      counterSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedEnemies]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [heroesData, mapsData, ranksData] = await Promise.all([
          fetchHeroes(),
          fetchMaps(),
          fetchRanks()
        ]);
        setHeroes(heroesData);
        setMaps(mapsData);
        setRanks(ranksData);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const counters = findBestCounters(
      selectedEnemies,
      bannedHeroes,
      selectedMap,
      selectedRank,
      selectedAllies
    );
    setCounterPicks(counters);

  }, [selectedEnemies, bannedHeroes, selectedMap, selectedRank, selectedAllies]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleCloseDisclaimer = () => {
    setDisclaimerOpen(false);
  };

  const handleOpenDisclaimer = () => {
    setDisclaimerOpen(true);
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          backgroundColor: '#2b3340',
          flexDirection: 'column',
          gap: 3
        }}
      >
        <CircularProgress sx={{ color: '#fbdb2b' }} size={60} />
        <Typography variant="h5" color="white">
          {t('loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#2b3340', minHeight: '100vh', py: 2 }}>
      <Container maxWidth="xl">
        <Box sx={{ my: 4 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ 
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              mb: 2,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80px',
                height: '4px',
                backgroundColor: '#fbdb2b',
                borderRadius: '2px'
              }
            }}
          >
            {t('appTitle')}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Button
              variant="text"
              size="small"
              startIcon={<InfoIcon />}
              onClick={handleOpenDisclaimer}
              sx={{ 
                color: 'rgba(255,255,255,0.7)',
                '&:hover': {
                  color: '#fbdb2b'
                }
              }}
            >
              {t('showDisclaimer')}
            </Button>
          </Box>
          
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ 
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              mb: 4,
              position: 'relative'
            }}
          >
            {/* Empty Typography for spacing */}
          </Typography>
          
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 3, 
                  mb: 3, 
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 2
                }}
              >
                <MapRankSelection
                  maps={maps}
                  ranks={ranks}
                  selectedMap={selectedMap}
                  selectedRank={selectedRank}
                  onMapChange={setSelectedMap}
                  onRankChange={setSelectedRank}
                />
              </Paper>

              <Paper 
                elevation={3} 
                sx={{ 
                  p: 3, 
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 2
                }}
              >
                <Box sx={{ borderBottom: 1, borderColor: 'rgba(255,255,255,0.2)', mb: 2 }}>
                  <Tabs 
                    value={selectedTab} 
                    onChange={handleTabChange} 
                    aria-label="hero selection tabs"
                    sx={{ 
                      '& .MuiTabs-indicator': { 
                        backgroundColor: '#fbdb2b' 
                      },
                      '& .MuiTab-root': { 
                        color: 'rgba(255,255,255,0.7)',
                        '&.Mui-selected': {
                          color: '#fbdb2b'
                        }
                      }
                    }}
                  >
                    <Tab label={t('enemyTeam')} />
                    <Tab label={t('alliedTeam')} />
                    <Tab label={t('bannedHeroesTab')} />
                  </Tabs>
                </Box>

                {selectedTab === 0 ? (
                  <HeroSelection
                    heroes={heroes}
                    selectedHeroes={selectedEnemies}
                    onHeroSelect={setSelectedEnemies}
                    bannedHeroes={bannedHeroes}
                    maxSelectable={6}
                    isEnemyTeam={true}
                    getRoleColor={getRoleColor}
                  />
                ) : selectedTab === 1 ? (
                  <HeroSelection
                    heroes={heroes}
                    selectedHeroes={selectedAllies}
                    onHeroSelect={setSelectedAllies}
                    bannedHeroes={bannedHeroes}
                    maxSelectable={6}
                    isEnemyTeam={false}
                    getRoleColor={getRoleColor}
                  />
                ) : (
                  <BannedHeroes
                    heroes={heroes}
                    bannedHeroes={bannedHeroes}
                    onBannedHeroesChange={setBannedHeroes}
                  />
                )}
              </Paper>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={3} 
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p:3 }}>
                  <Box>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2,
                        color: 'white',
                        fontWeight: 'bold',
                        position: 'relative',
                        paddingBottom: 1,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          width: '40px',
                          height: '3px',
                          backgroundColor: '#fbdb2b',
                          borderRadius: '2px'
                        }
                      }}
                    >
                      {t('enemyTeam')} ({selectedEnemies.length}/6)
                      {selectedEnemies.length > 0 && (
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          startIcon={<ClearIcon />}
                          onClick={() => setSelectedEnemies([])}
                          sx={{ 
                            ml: 'auto',
                            backgroundColor: 'rgba(211, 47, 47, 0.8)',
                            '&:hover': {
                              backgroundColor: 'rgba(211, 47, 47, 1)'
                            }
                          }}
                        >
                          {t('clear')}
                        </Button>
                      )}
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      {selectedEnemies.map((hero, index) => (
                        <Grid item xs={6} sm={4} md={4} key={hero.id}>
                          <Box
                            sx={{
                              backgroundColor: 'rgba(0,0,0,0.3)',
                              borderRadius: 2,
                              overflow: 'hidden',
                              border: '2px solid rgba(251, 219, 43, 0.3)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-5px)',
                                boxShadow: '0 5px 15px rgba(251, 219, 43, 0.2)'
                              }
                            }}
                          >
                            <Box sx={{ position: 'relative' }}>
                              <img
                                src={hero.image}
                                alt={hero.name}
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  display: 'block',
                                }}
                              />
                              <Box 
                                sx={{ 
                                  position: 'absolute', 
                                  top: 0, 
                                  right: 0, 
                                  backgroundColor: 'rgba(251, 219, 43, 0.9)',
                                  color: '#2b3340',
                                  fontWeight: 'bold',
                                  fontSize: '0.7rem',
                                  padding: '2px 6px',
                                  borderBottomLeftRadius: '8px'
                                }}
                              >
                                {t(hero.role.toLowerCase())}
                              </Box>
                            </Box>
                            <Typography
                              variant="body2"
                              align="center"
                              sx={{ 
                                display: 'block', 
                                py: 1,
                                color: 'white',
                                fontWeight: 'medium'
                              }}
                            >
                              {hero.name}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                      {selectedEnemies.length === 0 && (
                        <Grid item xs={12}>
                          <Box 
                            sx={{ 
                              p: 3, 
                              textAlign: 'center', 
                              backgroundColor: 'rgba(0,0,0,0.2)',
                              borderRadius: 2,
                              border: '1px dashed rgba(255,255,255,0.2)'
                            }}
                          >
                            <Typography variant="body1" color="rgba(255,255,255,0.7)">
                              {t('selectEnemyHeroesPrompt')}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {selectedAllies.length > 0 && (
                    <Box>
                      <Typography 
                        variant="h5" 
                        gutterBottom 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2,
                          color: 'white',
                          fontWeight: 'bold',
                          position: 'relative',
                          paddingBottom: 1,
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '40px',
                            height: '3px',
                            backgroundColor: '#fbdb2b',
                            borderRadius: '2px'
                          }
                        }}
                      >
                        {t('alliedTeam')} ({selectedAllies.length}/6)
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          startIcon={<ClearIcon />}
                          onClick={() => setSelectedAllies([])}
                          sx={{ 
                            ml: 'auto',
                            backgroundColor: 'rgba(211, 47, 47, 0.8)',
                            '&:hover': {
                              backgroundColor: 'rgba(211, 47, 47, 1)'
                            }
                          }}
                        >
                          {t('clear')}
                        </Button>
                      </Typography>
                    </Box>
                  )}

                  {counterPicks && (
                    <Box ref={counterSectionRef}>
                      <CounterDisplay
                        counterPicks={counterPicks}
                        heroes={heroes}
                        selectedEnemies={selectedEnemies}
                        selectedAllies={selectedAllies}
                        selectedMap={selectedMap}
                        selectedRank={selectedRank}
                      />
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <Typography variant="caption" color="rgba(255,255,255,0.6)" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
              {t('dataUpdated')}
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.6)" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
              {t('counterCalculation')}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
              <Button
                variant="text"
                size="small"
                startIcon={<InfoIcon />}
                onClick={handleOpenDisclaimer}
                sx={{ 
                  color: 'rgba(255,255,255,0.6)',
                  '&:hover': {
                    color: '#fbdb2b'
                  }
                }}
              >
                {t('showDisclaimer')}
              </Button>
            </Box>
            <Typography variant="caption" color="rgba(255,255,255,0.6)" sx={{ display: 'block', textAlign: 'center' }}>
              {t('copyright')}
            </Typography>
          </Box>
        </Box>

        {/* Disclaimer Dialog */}
        <Dialog
          open={disclaimerOpen}
          onClose={handleCloseDisclaimer}
          aria-labelledby="disclaimer-dialog-title"
          aria-describedby="disclaimer-dialog-description"
          sx={{
            '& .MuiDialog-paper': {
              backgroundColor: '#2b3340',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 3,
              maxWidth: 600,
              overflow: 'visible'
            }
          }}
        >
          <DialogTitle 
            id="disclaimer-dialog-title" 
            sx={{ 
              color: '#fbdb2b', 
              textAlign: 'center',
              fontWeight: 'bold',
              pt: 4,
              pb: 2,
              fontSize: '1.5rem',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '3px',
                backgroundColor: '#fbdb2b',
                borderRadius: '2px'
              }
            }}
          >
            {t('disclaimerTitle')}
          </DialogTitle>
          <DialogContent sx={{ px: 4, pb: 4, pt: 2 }}>
            <DialogContentText 
              id="disclaimer-dialog-description" 
              sx={{ 
                color: 'rgba(255,255,255,0.9)',
                textAlign: 'center',
                mb: 4,
                lineHeight: 1.6
              }}
            >
              {t('disclaimerText')}
            </DialogContentText>
            
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={handleCloseDisclaimer}
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: '#fbdb2b',
                  color: '#2b3340',
                  fontWeight: 'bold',
                  px: 4,
                  py: 1,
                  borderRadius: 2,
                  boxShadow: '0 4px 8px rgba(251, 219, 43, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#fbdb2b',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 12px rgba(251, 219, 43, 0.4)'
                  }
                }}
              >
                {t('disclaimerButton')}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
}

// Export MainContent for use in Home.js
export { MainContent };

function App() {
  return (
    <LanguageProvider>
      <Router>
        <NavBar />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/winrate" element={<WinRate />} />
          <Route path="/tierlist" element={<TierList />} />
        </Routes>
      </Router>
    </LanguageProvider>
  );
}

export default App;